import { MAX_NUM_OBJECTIVES } from "@app/constants/experiments";
import type {
  AlgorithmCreateInput,
  BatchConfigCreateInput,
  CategoricalParameterCreateInput,
  Chimera,
  ChimeraCreateInput,
  ConditionalExclusionConstraintCreateInput,
  ConditionalExclusionConstraintDefinitionCreateInput,
  ConstraintCreateInput,
  DatapointCreateInput,
  DatapointMeasurementValueCreateInput,
  DatapointParameterValueCreateInput,
  DatasetCreateInput,
  ExclusionConstraintBoundsCreateInput,
  ExclusionConstraintCreateInput,
  ExperimentCreateInput,
  ExperimentInputDatasetMappingCreateInput,
  ExperimentInputMeasurementMappingCreateInput,
  ExperimentInputParameterMappingCreateInput,
  ExpertContextCreateInput,
  GoalEnum,
  LinearConstraintCreateInput,
  LinearConstraintDefinitionCreateInput,
  LinearConstraintOperatorEnum,
  MeasurementCreateInput,
  NumericalParameterCreateInput,
  ObjectiveCreateInput,
  ParameterCategoryCreateInput,
  ParameterCategoryPropertyValueCreateInput,
  ParameterCreateInput,
  ParameterPropertyCreateInput,
  ParameterType,
  ParetoOptimization,
  ParetoOptimizationCreateInput,
  PatchedExperimentCreateInput,
  SubsetConstraintCreateInput,
  SubsetConstraintDefinitionCreateInput,
  SubsetConstraintOperatorEnum,
  WeightedSum,
  WeightedSumCreateInput
} from "@globals";
import { isNumber } from "lodash-es";
import type { GenericSchema } from "valibot";
import {
  array,
  boolean,
  check,
  forward,
  literal,
  maxLength,
  maxValue,
  minLength,
  minValue,
  nonNullish,
  nullable,
  nullish,
  number,
  object,
  optional,
  partial,
  partialCheck,
  picklist,
  pipe,
  string,
  union,
  variant
} from "valibot";

/* Parameter schemas */

export const parameterTypeSchema = picklist(["numerical", "categorical"]) satisfies GenericSchema<ParameterType>;

export const unitSchema = pipe(string(), maxLength(32, "Unit cannot by more than 32 characters."));

export const numericalParameterCreateInputSchema = pipe(
  object({
    id: optional(string()),
    name: pipe(string(), minLength(1, "Name is required."), maxLength(255, "Name must have less than 255 characters.")),
    description: optional(
      nullable(pipe(string(), maxLength(1024, "Description cannot by more than 1024 characters.")))
    ),
    unit: optional(nullable(unitSchema)),
    type: literal("numerical"),
    is_batch_constrained: boolean(),
    lower_bound: nonNullish(number("Minimum value is required."), "Minimum value is required."),
    upper_bound: nonNullish(number("Maximum value is required."), "Maximum value is required."),
    step_size: optional(
      nullable(
        pipe(
          number("Step size should be a number."),
          minValue(0.0001, "Step size must be at least 0.0001."),
          maxValue(100, "Step size cannot exceed 100.")
        )
      )
    ),
    design_space_parameter_id: optional(nullable(string()))
  }),
  forward(
    partialCheck(
      [["lower_bound"], ["upper_bound"]],
      input => input.lower_bound <= input.upper_bound,
      "Minimum value should be less than Maximum value."
    ),
    ["upper_bound"]
  ),
  forward(
    partialCheck(
      [["lower_bound"], ["upper_bound"], ["step_size"]],
      input => input.step_size == null || input.upper_bound - input.lower_bound >= input.step_size,
      "Step size is too large for the range. Range must be at least equal to step size."
    ),
    ["step_size"]
  )
) satisfies GenericSchema<NumericalParameterCreateInput>;

export const parameterCategoryPropertyValueCreateInputSchema = variant(
  // Handle cases where the value is initialized as undefined in the form
  "value",
  [
    object({
      category_id: pipe(string(), minLength(1, "Option is required.")),
      value: number("The option property value must be a number.")
    })
  ],
  "Missing value for category property."
) satisfies GenericSchema<ParameterCategoryPropertyValueCreateInput>;

export const parameterCategoryCreateInputSchema = object({
  name: pipe(
    string(),
    minLength(1, "Option name is required."),
    maxLength(255, "Option name must have less than 255 characters.")
  ),
  id: string()
}) satisfies GenericSchema<ParameterCategoryCreateInput>;

export const parameterPropertyCreateInputSchema = object({
  id: optional(string()),
  name: pipe(
    string(),
    minLength(1, "Property name is required."),
    maxLength(255, "Property name must have less than 255 characters.")
  ),
  category_property_values: array(parameterCategoryPropertyValueCreateInputSchema)
}) satisfies GenericSchema<ParameterPropertyCreateInput>;

export const categoricalParameterCreateInputSchema = pipe(
  object({
    id: optional(string()),
    name: pipe(string(), minLength(1, "Name is required."), maxLength(255, "Name must have less than 255 characters.")),
    description: optional(
      nullable(pipe(string(), maxLength(1024, "Description cannot by more than 1024 characters.")))
    ),
    unit: optional(nullable(unitSchema)),
    type: literal("categorical"),
    is_batch_constrained: boolean(),
    categories: pipe(
      array(parameterCategoryCreateInputSchema),
      minLength(1, "At least one option is required for categorical variables.")
    ),
    properties: optional(array(parameterPropertyCreateInputSchema)),
    design_space_parameter_id: optional(nullable(string()))
  }),
  forward(
    partialCheck(
      [["categories"], ["properties"]],
      ({ categories, properties }) => {
        if (categories.length > 0 && properties && properties.length > 0) {
          // Check if each property has values for each category.
          for (const property of properties) {
            for (const category of categories) {
              const hasValue = property.category_property_values?.some(
                cv => cv.category_id === category.id && typeof cv.value === "number"
              );
              if (!hasValue) {
                return false; // If any property does not have a value for a category, return false.
              }
            }
          }
        }
        return true;
      },
      "All properties must have values for each category."
    ),
    ["properties"]
  )
) satisfies GenericSchema<CategoricalParameterCreateInput>;

export const genericParameterCreateInputSchema = union([
  numericalParameterCreateInputSchema,
  categoricalParameterCreateInputSchema
]) satisfies GenericSchema<ParameterCreateInput>;

/* END Parameter schemas */

/* Objective / Measurement schemas */

export const goalEnumSchema = picklist(["maximize", "minimize", "target"]) satisfies GenericSchema<GoalEnum>;

export const chimeraCreateInputSchema = object({
  hierarchy: number(),
  tolerance: number()
}) satisfies GenericSchema<ChimeraCreateInput>;

export const chimeraSchema = object({
  id: string(),
  created_at: string(),
  updated_at: string(),
  ...chimeraCreateInputSchema.entries
}) satisfies GenericSchema<Chimera>;

export const paretoOptimizationCreateInputSchema = object({
  threshold: nullable(number())
}) satisfies GenericSchema<ParetoOptimizationCreateInput>;

export const paretoOptimizationSchema = object({
  id: string(),
  created_at: string(),
  updated_at: string(),
  ...paretoOptimizationCreateInputSchema.entries
}) satisfies GenericSchema<ParetoOptimization>;

export const weightedSumCreateInputSchema = object({
  weight: number("weight is required for weighted sum objective.")
}) satisfies GenericSchema<WeightedSumCreateInput>;

export const weightedSumSchema = object({
  id: string(),
  created_at: string(),
  updated_at: string(),
  weight: number("weight is required for weighted sum objective.")
}) satisfies GenericSchema<WeightedSum>;

export const objectiveCreateInputSchema = pipe(
  object({
    goal: goalEnumSchema,
    target: optional(nullable(number("Target value is required for target goal."))),
    weighted_sum: optional(nullable(weightedSumCreateInputSchema)),
    pareto_optimization: optional(
      nullable(
        object({
          threshold: nullable(number())
        })
      )
    ),
    chimera: optional(
      nullable(
        object({
          hierarchy: number(),
          tolerance: number()
        })
      )
    )
  }),
  forward(
    partialCheck(
      [["goal"], ["target"]],
      input => input.goal !== ("target" satisfies GoalEnum) || isNumber(input.target),
      "Target value is required for target goal type."
    ),
    ["target"]
  )
) satisfies GenericSchema<ObjectiveCreateInput>;

export const measurementCreateInputSchema = object({
  id: optional(string()),
  name: pipe(
    string(),
    minLength(1, "Result name is required."),
    maxLength(255, "Name must have less than 255 characters.")
  ),
  description: optional(nullable(pipe(string(), maxLength(1024, "Description cannot by more than 1024 characters.")))),
  unit: optional(nullable(unitSchema)),
  objective: optional(nullable(objectiveCreateInputSchema)),
  design_space_measurement_id: optional(nullable(string()))
}) satisfies GenericSchema<MeasurementCreateInput>;

export const batchConfigCreateInputSchema = object({
  size: pipe(number(), minValue(1, "Batch size must be at least 1."), maxValue(64, "Batch size cannot exceed 64."))
}) satisfies GenericSchema<BatchConfigCreateInput>;

/* END Objective / Measurement schemas */

/* Algorithm schemas */

export const expertContextCreateInputSchema = object({
  content: nullish(string())
}) satisfies GenericSchema<ExpertContextCreateInput>;

export const algorithmCreateInputSchema = object({
  random_seed: number(),
  expert_context: nullable(expertContextCreateInputSchema),
  categorical_descriptors: boolean()
}) satisfies GenericSchema<AlgorithmCreateInput>;

/* END Algorithm schemas */

/* Constraint schemas */

export const exclusionConstraintBoundCreateInputSchema = object({
  lower_bound: nullable(number()),
  upper_bound: nullable(number()),
  category: nullable(string())
}) satisfies GenericSchema<ExclusionConstraintBoundsCreateInput>;

export const conditionalExclusionConstraintDefinitionCreateInputSchema = object({
  parameter_id: string(),
  bounds: pipe(
    array(exclusionConstraintBoundCreateInputSchema),
    minLength(1, "At least one exclusion definition is required.")
  )
}) satisfies GenericSchema<ConditionalExclusionConstraintDefinitionCreateInput>;

export const conditionalExclusionConstraintCreateInputSchema = object({
  id: optional(string()),
  name: nullable(pipe(string(), maxLength(255, "Name must have less than 255 characters."))),
  definitions: pipe(
    array(conditionalExclusionConstraintDefinitionCreateInputSchema),
    minLength(2, "At least two exclusions are required.")
  ),
  constraint_type: literal("conditional_exclusion")
}) satisfies GenericSchema<ConditionalExclusionConstraintCreateInput>;

export const exclusionConstraintCreateInputSchema = object({
  id: optional(string()),
  name: nullable(pipe(string(), maxLength(255, "Name must have less than 255 characters."))),
  parameter_id: string(),
  bounds: pipe(array(exclusionConstraintBoundCreateInputSchema), minLength(1)),
  constraint_type: literal("exclusion")
}) satisfies GenericSchema<ExclusionConstraintCreateInput>;

const linearConstraintDefinitionCreateInputSchema = object({
  parameter_id: pipe(string(), minLength(1, "Variable is required.")),
  weight: number()
}) satisfies GenericSchema<LinearConstraintDefinitionCreateInput>;

const linearConstraintOperatorEnumSchema = picklist([
  "equal",
  "less_than_equal",
  "greater_than_equal",
  "between"
]) satisfies GenericSchema<LinearConstraintOperatorEnum>;

export const linearConstraintCreateInputSchema = pipe(
  object({
    id: optional(string()),
    name: nullable(pipe(string(), maxLength(255, "Name must have less than 255 characters."))),
    definitions: pipe(
      array(linearConstraintDefinitionCreateInputSchema),
      minLength(2, "At least two definitions are required.")
    ),
    constraint_type: literal("linear"),
    operator: linearConstraintOperatorEnumSchema,
    sum: nullable(number()),
    lower_bound: nullable(number()),
    upper_bound: nullable(number())
  }) satisfies GenericSchema<LinearConstraintCreateInput>,
  forward(
    partialCheck(
      [["operator"], ["sum"]],
      input => input.operator === "between" || isNumber(input.sum),
      "Sum is required for 'equal', 'greater_than_equal' and 'less_than_equal' operators."
    ),
    ["sum"]
  ),
  forward(
    partialCheck(
      [["operator"], ["lower_bound"], ["upper_bound"]],
      input => input.operator !== "between" || (isNumber(input.lower_bound) && isNumber(input.upper_bound)),
      "Lower and upper bounds are required for 'between' operator."
    ),
    ["upper_bound"]
  )
);

export const subsetConstraintOperatorEnumSchema = picklist([
  "equal",
  "less_than_equal",
  "greater_than_equal",
  "between"
]) satisfies GenericSchema<SubsetConstraintOperatorEnum>;

export const subsetConstraintDefinitionCreateInputSchema = object({
  parameter_id: string(),
  fixed_numerical_value: number()
}) satisfies GenericSchema<SubsetConstraintDefinitionCreateInput>;

export const subsetConstraintCreateInputSchema = pipe(
  object({
    id: optional(string()),
    name: nullable(pipe(string(), maxLength(255, "Name must have less than 255 characters."))),
    definitions: array(subsetConstraintDefinitionCreateInputSchema),
    constraint_type: literal("subset"),
    operator: subsetConstraintOperatorEnumSchema,
    number_of_active_parameters: nullable(number()),
    lower_bound: nullable(number()),
    upper_bound: nullable(number())
  }) satisfies GenericSchema<SubsetConstraintCreateInput>,
  forward(
    partialCheck(
      [["operator"], ["number_of_active_parameters"]],
      input => input.operator === "between" || isNumber(input.number_of_active_parameters),
      "Number of active variables is required for equal, greater_than, less_than operators."
    ),
    ["number_of_active_parameters"]
  ),
  forward(
    partialCheck(
      [["operator"], ["lower_bound"], ["upper_bound"]],
      input => input.operator !== "between" || (isNumber(input.lower_bound) && isNumber(input.upper_bound)),
      "Lower and upper bounds are required for between operator."
    ),
    ["upper_bound"]
  )
);

export const constraintCreateInputSchema = union([
  exclusionConstraintCreateInputSchema,
  conditionalExclusionConstraintCreateInputSchema,
  linearConstraintCreateInputSchema,
  subsetConstraintCreateInputSchema
]) satisfies GenericSchema<ConstraintCreateInput>;

/* END Constraints schemas */

/* Experiment schemas */

export const experimentInputParameterMappingCreateInputSchema = object({
  source_parameter_id: nullable(string()),
  destination_parameter_id: string(),
  fixed_text_value: optional(nullable(string())),
  fixed_numerical_value: optional(nullable(number()))
}) satisfies GenericSchema<ExperimentInputParameterMappingCreateInput>;

export const experimentInputMeasurementMappingCreateInputSchema = object({
  source_measurement_id: nullable(string()),
  destination_measurement_id: string(),
  fixed_value: optional(nullable(number()))
}) satisfies GenericSchema<ExperimentInputMeasurementMappingCreateInput>;

export const experimentInputDatasetMappingCreateInputSchema = object({
  dataset_id: string(),
  parameter_mappings: array(experimentInputParameterMappingCreateInputSchema),
  measurement_mappings: array(experimentInputMeasurementMappingCreateInputSchema)
}) satisfies GenericSchema<ExperimentInputDatasetMappingCreateInput>;

export const experimentCreateInputSchema = object({
  name: pipe(
    string(),
    minLength(1, "Experiment name is required."),
    maxLength(255, "Name must have less than 255 characters.")
  ),
  description: optional(nullable(pipe(string(), maxLength(1024, "Description must have less than 1024 characters.")))),
  tags: optional(array(object({ name: string() }))),
  parameters: pipe(
    array(genericParameterCreateInputSchema),
    check(parameters => parameters.length > 0, "At least one variable is required."),
    check(parameters => {
      const names = parameters.map(p => p.name.toLowerCase().trim());
      return names.length === new Set(names).size;
    }, "Variable names must be unique.")
  ),
  measurements: pipe(
    array(measurementCreateInputSchema),
    check(measurements => measurements.length > 0, "At least one result is required."),
    check(measurements => measurements.some(m => m.objective), "At least one of the results must have an objective."),
    check(measurements => {
      const names = measurements.map(m => m.name.toLowerCase().trim());
      return names.length === new Set(names).size;
    }, "Result names must be unique."),
    check(measurements => {
      const numObjectives = measurements.filter(m => m.objective != null).length;
      return numObjectives <= MAX_NUM_OBJECTIVES;
    }, `Number of objectives cannot be more than ${MAX_NUM_OBJECTIVES}.`)
  ),
  constraints: array(constraintCreateInputSchema),
  batch_config: batchConfigCreateInputSchema,
  historical_datasets: optional(array(experimentInputDatasetMappingCreateInputSchema)),
  algorithm: algorithmCreateInputSchema,
  design_space_id: optional(nullable(string()))
}) satisfies GenericSchema<ExperimentCreateInput>;

export const patchedExperimentCreateInputSchema = partial(
  experimentCreateInputSchema
) satisfies GenericSchema<PatchedExperimentCreateInput>;

/* END Experiment schemas */

/* Dataset schemas */

export const parameterValueCreateInputSchema = object({
  parameter_id: string(),
  executed_text_value: nullable(string()),
  executed_numerical_value: nullable(number())
}) satisfies GenericSchema<DatapointParameterValueCreateInput>;

export const measurementValueCreateInputSchema = object({
  measurement_id: string(),
  value: number()
}) satisfies GenericSchema<DatapointMeasurementValueCreateInput>;

export const datapointCreateInputSchema = object({
  parameter_values: array(parameterValueCreateInputSchema),
  measurement_values: array(measurementValueCreateInputSchema),
  invalid: optional(boolean())
}) satisfies GenericSchema<DatapointCreateInput>;

export const datasetCreateInputSchema = object({
  name: pipe(
    string(),
    minLength(1, "Dataset name is required."),
    maxLength(255, "Name cannot be more than 255 characters.")
  ),
  parameters: pipe(
    array(genericParameterCreateInputSchema),
    check(parameters => parameters.length > 0, "At least one variable is required.")
  ),
  measurements: pipe(
    array(measurementCreateInputSchema),
    check(measurements => measurements.length > 0, "At least one result is required.")
  ),
  datapoints: array(datapointCreateInputSchema)
}) satisfies GenericSchema<DatasetCreateInput>;

/* END Experiment schemas */
